import { Tooltip, TooltipProps } from 'primereact/tooltip'
import PrimeIcon from './PrimeIcon'
import Bolt, {
    BoltMeasurementProgressType,
    BoltMeasurementProgressTypes,
} from '../../../models/Bolt'
import t, { _ta, _tf } from '../../../tools/t'
import ClassNameBuilder from '../../../tools/ClassNameBuilder'
import { Row } from './FlexContainer'

const DEFAULT_SHOW_DELAY = 500

interface TooltipWrapperProps extends TooltipProps {
    label: string
    target?: string
    disabled?: boolean
}

export default function TooltipWrapper({
    label,
    target,
    disabled,
    children,
    ...props
}: TooltipWrapperProps) {
    target = (target ?? label)
        // Replace all whitespace and non-alphanumeric characters with a single dash.
        .replaceAll(/\s+/g, '-')
        .replaceAll(/\W+/g, '-')
        // Replace all pipe characters, which are illegal selector characters.
        // NOTE: Auth0 ids contains a pipe (|) symbol.
        .replaceAll(/\|.+$/g, '')
        // Lowercase for good measure.
        .toLowerCase()
    if (!props.showDelay) props.showDelay = DEFAULT_SHOW_DELAY
    const tooltipTargetClass = `tooltip-wrapper-${target}`
    const tooltipTargetSelector = `.${tooltipTargetClass}`

    // NOTE: Always return wrapped children to avoid spreading multiple children into a parent where only the tooltip wrapper is expected.
    if (disabled) return <span>{children}</span>

    return (
        <>
            <Tooltip {...props} target={tooltipTargetSelector} content={label} />
            <span className={tooltipTargetClass}>{children}</span>
        </>
    )
}

interface TooltipIconProps extends TooltipWrapperProps {
    name: PrimeIconName
    iconClassName?: string
}
export const TooltipIcon = ({ name, iconClassName, ...props }: TooltipIconProps) => (
    <TooltipWrapper {...props} position="bottom">
        <PrimeIcon name={name} className={iconClassName} />
    </TooltipWrapper>
)

interface BoltMeasurementProgressTooltipRowProps {
    bolt: Bolt
}
export const BoltMeasurementProgressTooltipRow = ({
    bolt,
}: BoltMeasurementProgressTooltipRowProps) => {
    const progressIconOptions = [
        BoltMeasurementProgressTypes.Reference,
        // BoltMeasurementProgressTypes.Assigned,
        BoltMeasurementProgressTypes.Elongation,
    ]
    const elongations = bolt.elongationCount
    return (
        <Row
            className="bolt-measurement-progress"
            arrangement="spaced"
            alignment="center"
            gap={0.5}
        >
            {progressIconOptions.map((type) => (
                <BoltMeasurementProgressTooltipIcon
                    key={type}
                    type={type}
                    target={bolt.id + type}
                    disabled={!bolt.typeSummary.includes(type)}
                />
            ))}
            {/* NOTE: Elongations are counted at the end of the row, because the elongation icon is last */}
            {elongations > 1 && (
                <div className="bolt-measurement-progress__elongations">
                    x{bolt.elongationCount}
                </div>
            )}
        </Row>
    )
}

interface BoltMeasurementProgressTooltipIconProps {
    target: string
    type?: BoltMeasurementProgressType
    disabled?: boolean
}
export const BoltMeasurementProgressTooltipIcon = ({
    target,
    type,
    disabled = false,
}: BoltMeasurementProgressTooltipIconProps) => {
    const classNames = new ClassNameBuilder()
        .add('bolt-measurement-progress__icon')
        .addIf('bolt-measurement-progress__icon--disabled', disabled)
        .addIf(
            'bolt-measurement-progress__icon--custom-elongation-caret',
            type === BoltMeasurementProgressTypes.Elongation
        )

    switch (type) {
        case BoltMeasurementProgressTypes.Assigned:
            return (
                <TooltipIcon
                    iconClassName={classNames.toString()}
                    target={target + type}
                    name="pencil"
                    disabled={disabled}
                    label={t('measurement_type_assigned')}
                />
            )
        case BoltMeasurementProgressTypes.Reference:
            return (
                <TooltipIcon
                    iconClassName={classNames.toString()}
                    target={target + type}
                    name="arrows-v"
                    disabled={disabled}
                    label={t('measurement_type_reference')}
                />
            )
        case BoltMeasurementProgressTypes.Elongation:
            return (
                <TooltipIcon
                    iconClassName={classNames.toString()}
                    target={target + type}
                    name="caret-up"
                    disabled={disabled}
                    label={t('measurement_type_elongation')}
                />
            )
        case BoltMeasurementProgressTypes.Archived:
            return (
                <TooltipIcon
                    iconClassName={classNames.toString()}
                    target={target + type}
                    name="folder"
                    disabled={disabled}
                    label={t('measurement_type_archived')}
                />
            )
        default:
            return (
                <TooltipIcon
                    iconClassName={classNames.toString()}
                    target={target + 'unknown'}
                    name="question"
                    disabled={disabled}
                    label={type ?? t('common_unknown')}
                />
            )
    }
}
